import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { UtilService } from '../../../../shared/services/util.service';
import { TagCompraService } from '../../../../shared/services/apis/compras/tag-compra.service';
import { TagCompra } from '../../../../shared/services/models/compras/tag-compra.model';

@Component({
  selector: 'app-tags-compra',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './tags-compra.component.html',
  styleUrl: './tags-compra.component.css',
})
export class TagsCompraComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;
  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;
  public _dataSource = new DataSourceOrdenacaoInsensivel<TagCompra>();
  public _isLoading: boolean = false;
  public displayedColumns: string[] = ['select', 'descricao', 'actions'];
  public selection = new SelectionModel<TagCompra>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private tagService: TagCompraService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({ pesquisa: [''] });
    this.loadFilters();
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/suprimentos/tags-compra/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarDados(reset: boolean = false): void {
    this._isLoading = true;
    const descricao = this.model.get('pesquisa')?.value || '';
    this.tagService.getAll(descricao, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar tags',
            AlertType.error
          );
          return;
        }
        this._totalRegistros = response.totalRegistros;
        if (reset) {
          this._dataSource.data = response.listaTags;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaTags];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar tags', AlertType.error);
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarDados();
  }
  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this.selection.clear();
    this._dataSource.data = [];
    this.carregarDados(true);
  }
  public isAllSelected() {
    return (
      this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0
    );
  }
  public toggleAllRows() {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: TagCompra): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];
    if (itemsToDelete.length === 0) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description:
          'As tags selecionadas serão excluídas. Caso existam compras vinculadas, elas serão desvinculadas. Confirma a exclusão?',
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;
        if (isBulk) {
          this.tagService.deleteBulk(itemsToDelete.map(i => i.idTagCompra)).subscribe({
            next: r => {
              this._isLoading = false;
              if (r.isError) {
                this.alertService.show('Atenção', r.errorDescription, AlertType.warning);
              } else {
                this.alertService.show('Sucesso', 'Tags excluídas com sucesso', AlertType.success);
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show('Erro', err.message, AlertType.error);
            },
          });
        } else {
          this.tagService.delete(item!.idTagCompra).subscribe({
            next: r => {
              this._isLoading = false;
              if (r.isError) {
                this.alertService.show('Erro', r.errorDescription, AlertType.error);
              } else {
                this.alertService.show('Sucesso', 'Tag excluída com sucesso', AlertType.success);
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show('Erro', err.message, AlertType.error);
            },
          });
        }
      }
    });
  }
}
